import i18n from 'i18next';
import {match, Pattern} from 'ts-pattern';

import {always, isNotNil} from 'ramda';

import {isNotNilOrZero} from 'shared/web';

/**
 * Formats a TIME_INTERVAL cell value (seconds) as a human-readable duration,
 * e.g. "2 days, 3 hours, 5 minutes".
 *
 * Unit labels are resolved lazily (`() => i18n.t(...)`) so only the matched
 * plural bucket pays the lookup, and the sub-minute early return pays none.
 */
export const formatTimeToResponse = (timeInSeconds: number): string => {
  const absoluteTimeInSeconds = Math.abs(timeInSeconds);
  const {days, hours, minutes, totalMinutes} = destructureTime(absoluteTimeInSeconds);

  if (totalMinutes < 1) {
    return i18n.t('general.labels.lessThanAMinute');
  }

  const dayUnit = match(days)
    .with(Pattern.nullish, 0, always(null))
    .with(1, () => i18n.t('general.labels.1day').toLowerCase())
    .with(2, 3, 4, () => i18n.t('general.labels.234days').toLowerCase())
    .otherwise(() => i18n.t('general.labels.moreDays').toLowerCase());

  const hourUnit = match(hours)
    .with(Pattern.nullish, 0, always(null))
    .with(1, () => i18n.t('general.labels.singleHour').toLowerCase())
    .otherwise(() => i18n.t('general.labels.hours').toLowerCase());

  const minuteUnit = match(minutes)
    .with(Pattern.nullish, 0, always(null))
    .with(1, () => i18n.t('general.labels.minute').toLowerCase())
    .otherwise(() => i18n.t('general.labels.minutes').toLowerCase());

  const formattedDays = isNotNilOrZero(days) ? `${days} ${dayUnit}` : null;
  const formattedHours = isNotNilOrZero(hours) ? `${hours} ${hourUnit}` : null;
  const formattedMinutes = isNotNilOrZero(minutes) ? `${minutes} ${minuteUnit}` : null;

  return [formattedDays, formattedHours, formattedMinutes].filter(isNotNil).join(', ');
};

const destructureTime = (timeInSeconds: number) => {
  const totalMinutes = Math.floor(timeInSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  return {hours, minutes, days, totalMinutes};
};
